import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useOutletContext } from 'react-router-dom'
import { debounce } from 'lodash'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'

import { useDeleteIglooVisitorKey } from '@/api/keyless/igloo/delete-igloo-visitor-key'
import { getApiErrorMessage } from '@/utils/error'

import FeaturedTable from '../../FeaturedTable/FeaturedTable'
import SearchField from '../../FormLegacy/FormFieldsV2/SearchField'
import TextAreaField from '../../FormLegacy/FormFieldsV2/TextAreaField'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import PaginationFooter from '../../PaginationFooter/PaginationFooter'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import GuestDetailsModal from '../modals/VisitorDetailsModal/GuestDetailsModal'
import API from '../../../api/api'
import ModalsContainer from '../modals/ModalsContainer'
import { VistorModalTypes } from '../modals/utils'
import { readListEnvelope } from '../utils'

import { getGuestColumns, getWriteErrorMessage } from './helpers'

const Guests = ({ selectedBuilding }) => {
  const { t } = useTranslation(['visitors', 'common'])
  const visitorStatuses = [
    { text: t('visitors:statuses.all'), value: 'all' },
    { text: t('visitors:statuses.active'), value: 'active' },
    { text: t('visitors:statuses.expired'), value: 'expired' },
    { text: t('visitors:statuses.banned'), value: 'banned' },
    { text: t('visitors:statuses.suspended'), value: 'suspended' },
  ]
  const typeLabel = t('visitors:snackbar.typeGuest')
  const { analyticsOpen, newVisitorModalOpen, setNewVisitorModalOpen } = useOutletContext()
  const [guests, setGuests] = useState([])
  const [banGuestState, setBanGuestState] = useState(false)
  const [unbanGuestState, setUnbanGuestState] = useState(false)
  const [banReason, setBanReason] = useState('')
  const [checkInGuestState, setCheckInGuestState] = useState(false)
  const [isBanReasonError, setIsBanReasonError] = useState(false)
  const [search, setSearch] = useState('')
  const [isLoading, setLoading] = useState(false)
  const [isGuestsLoading, setIsGuestsLoading] = useState(false)
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [selectedTab, setSelectedTab] = useState(visitorStatuses[0])
  const [visitorDetailsId, setVisitorDetailsId] = useState(null)
  const { displaySnackbar } = useSnackbar()
  const [suspendGuestState, setSuspendGuestState] = useState(false)
  const [unsuspendGuestState, setUnsuspendGuestState] = useState(false)
  const [editingGuest, setEditingGuest] = useState(null)
  const [deleteGuestKeyState, setDeleteGuestKeyState] = useState(false)
  const { mutateAsync: deleteIglooVisitorKey } = useDeleteIglooVisitorKey()

  const onBanToggle = (guest) => {
    if (guest.banned) {
      setUnbanGuestState(guest)
    } else {
      setBanGuestState(guest)
      setBanReason('')
    }
  }

  const onCheckInClick = (guest) => {
    setCheckInGuestState(guest)
  }

  const onDetailsClick = (visitor) => {
    setVisitorDetailsId(visitor.id)
  }

  const onEditGuest = (guest) => {
    if (guest.banned || guest.suspended) {
      displaySnackbar(t('visitors:snackbar.cannotEditBannedOrSuspended'), true)
      return
    }

    setEditingGuest(guest)
    setNewVisitorModalOpen(VistorModalTypes.GuestTypeModal)
  }

  const onDeleteGuestKey = (guest) => {
    setDeleteGuestKeyState(guest)
  }

  const onDeleteGuestKeyConfirm = async () => {
    setLoading(true)
    try {
      await deleteIglooVisitorKey({
        clientId: deleteGuestKeyState.clientId,
        guestId: deleteGuestKeyState.id,
      })
      displaySnackbar(t('visitors:snackbar.iglooKeyRevoked'))
    } catch (err) {
      Sentry.captureException(err)
      // Note: authAPI's interceptor already lifted the server message onto err.message.
      displaySnackbar(getApiErrorMessage(err), true)
    } finally {
      setDeleteGuestKeyState(false)
      setLoading(false)
      fetchGuests(search)
    }
  }

  const onSuspendToggle = (guest) => {
    if (guest.suspended) {
      setUnsuspendGuestState(guest)
    } else {
      setSuspendGuestState(guest)
    }
  }

  const onBanConfirm = async () => {
    if (!banReason) {
      setIsBanReasonError(true)
      return
    }

    setLoading(true)
    try {
      await API.updateVisitorStatus({
        id: banGuestState.id,
        type: 'guest',
        banned: true,
        bannedReason: banReason,
      })
      displaySnackbar(t('visitors:snackbar.banned', { type: typeLabel, name: banGuestState.name }))
    } catch (err) {
      Sentry.captureException(err)
      displaySnackbar(
        getWriteErrorMessage(err, t('visitors:snackbar.errorBanning', { type: typeLabel })),
        true,
      )
    } finally {
      setBanGuestState(false)
      setBanReason('')
      setLoading(false)
      setIsBanReasonError(false)
      fetchGuests(search)
    }
  }

  const onUnbanConfirm = async () => {
    setLoading(true)
    try {
      await API.updateVisitorStatus({
        id: unbanGuestState.id,
        type: 'guest',
        banned: false,
        bannedReason: '',
      })
      displaySnackbar(
        t('visitors:snackbar.unbanned', { type: typeLabel, name: unbanGuestState.name }),
      )
    } catch (err) {
      Sentry.captureException(err)
      displaySnackbar(
        getWriteErrorMessage(err, t('visitors:snackbar.errorUnbanning', { type: typeLabel })),
        true,
      )
    } finally {
      setUnbanGuestState(false)
      setLoading(false)
      fetchGuests(search)
    }
  }

  const onCheckInConfirm = async () => {
    setLoading(true)
    try {
      await API.checkInClientGuest(checkInGuestState.clientId, checkInGuestState.id)
      displaySnackbar(
        t('visitors:snackbar.checkedIn', { type: typeLabel, name: checkInGuestState.name }),
      )
    } catch (err) {
      Sentry.captureException(err)
      displaySnackbar(
        getWriteErrorMessage(err, t('visitors:snackbar.errorCheckingIn', { type: typeLabel })),
        true,
      )
    } finally {
      setCheckInGuestState(false)
      setLoading(false)
      fetchGuests(search)
    }
  }

  const onSuspendConfirm = async () => {
    setLoading(true)
    try {
      await API.updateVisitorStatus({
        id: suspendGuestState.id,
        type: 'guest',
        suspended: true,
      })
      displaySnackbar(
        t('visitors:snackbar.suspended', { type: typeLabel, name: suspendGuestState.name }),
      )
    } catch (err) {
      Sentry.captureException(err)
      displaySnackbar(
        getWriteErrorMessage(err, t('visitors:snackbar.errorSuspending', { type: typeLabel })),
        true,
      )
    } finally {
      setSuspendGuestState(false)
      setLoading(false)
      fetchGuests(search)
    }
  }

  const onUnsuspendConfirm = async () => {
    setLoading(true)
    try {
      await API.updateVisitorStatus({
        id: unsuspendGuestState.id,
        type: 'guest',
        suspended: false,
      })
      displaySnackbar(
        t('visitors:snackbar.unsuspended', { type: typeLabel, name: unsuspendGuestState.name }),
      )
    } catch (err) {
      Sentry.captureException(err)
      displaySnackbar(
        getWriteErrorMessage(err, t('visitors:snackbar.errorUnsuspending', { type: typeLabel })),
        true,
      )
    } finally {
      setUnsuspendGuestState(false)
      setLoading(false)
      fetchGuests(search)
    }
  }

  const fetchGuests = async (searchTerm = search) => {
    if (!selectedBuilding) {
      return
    }
    setIsGuestsLoading(true)
    const skip = (pageNumber - 1) * showOnPage
    try {
      await API.getGuests({
        buildingId: selectedBuilding.id || '1',
        filter: selectedTab.value !== 'all' ? selectedTab.value : undefined,
        search: searchTerm,
        skip,
        limit: showOnPage,
      }).then((res) => {
        const { items, total } = readListEnvelope(res)
        setGuests(items)
        setPaginationItemsCount(total)
      })
    } catch (err) {
      Sentry.captureException(err)
      displaySnackbar(t('visitors:snackbar.errorFetchingGuests'), true)
    } finally {
      setIsGuestsLoading(false)
    }
  }

  const debouncedFetchGuests = debounce((searchTerm) => {
    fetchGuests(searchTerm)
  }, 500)

  const handleSearchChange = (e) => {
    const searchTerm = e.target.value
    setSearch(searchTerm)
    setPageNumber(1)
    debouncedFetchGuests(searchTerm)
  }

  useEffect(() => {
    fetchGuests()
  }, [pageNumber, showOnPage, selectedTab, selectedBuilding])

  const columns = getGuestColumns(
    t,
    onCheckInClick,
    onDetailsClick,
    onBanToggle,
    onSuspendToggle,
    onEditGuest,
    selectedBuilding ? selectedBuilding.timezone : 'America/Chicago',
    onDeleteGuestKey,
    selectedBuilding?.iglooWorkAvailable,
    selectedBuilding?.iglooWorkVisitorAccessAvailable,
  )

  const maxTableHeight = {
    maxHeight: analyticsOpen ? `calc(100vh - 550px)` : `calc(100vh - 400px)`,
  }

  return (
    <div className="visitors">
      <div className="visitors__navbar">
        <div className="visitors__navbar-tabs">
          {visitorStatuses.map((tab) => (
            <div
              className={`visitors__navbar-tab ${tab.value === selectedTab.value ? 'active' : ''}`}
              key={tab.value}
              onClick={() => setSelectedTab(tab)}
            >
              {tab.text}
            </div>
          ))}
        </div>
        <SearchField
          placeholder={t('visitors:toolbar.searchByNameOrEmail')}
          value={search}
          onChange={handleSearchChange}
        />
      </div>
      <div
        className={`ctp-visitors__table ${isGuestsLoading || isLoading ? 'is-loading' : ''}`}
        style={maxTableHeight}
      >
        {guests?.length > 0 ? (
          <FeaturedTable
            columns={columns}
            data={guests}
            onRowClick={(row) => onDetailsClick(row.original)}
          />
        ) : (
          <div className="no-results">{t('visitors:views.noGuests')}</div>
        )}
      </div>
      {guests?.length > 0 && (
        <PaginationFooter
          showOnPage={showOnPage}
          pageNumber={pageNumber}
          paginationItemsCount={paginationItemsCount}
          setSiteLoading={setIsGuestsLoading}
          setShowOnPage={setShowOnPage}
          setPageNumber={setPageNumber}
        />
      )}
      <ConfirmPopup
        title={t('visitors:confirm.confirmationTitle')}
        body={
          <div>
            <p>{t('visitors:confirm.banBody', { type: typeLabel, name: banGuestState.name })}</p>
            <div className="ban-reason-container">
              <TextAreaField
                name="ban-reason"
                label={t('visitors:columns.banReasonLabel')}
                placeholder={t('visitors:columns.banReasonPlaceholder', { type: typeLabel })}
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                isError={isBanReasonError}
              />
            </div>
          </div>
        }
        cancelText={t('common:actions.cancel')}
        confirmText={t('visitors:confirm.yesBan')}
        isOpen={!!banGuestState}
        cancelCallback={() => setBanGuestState(false)}
        confirmCallback={onBanConfirm}
        isLoading={isLoading}
      />
      <ConfirmPopup
        title={t('visitors:confirm.confirmationTitle')}
        body={
          unbanGuestState
            ? t('visitors:confirm.unbanBody', { type: typeLabel, name: unbanGuestState.name })
            : ''
        }
        cancelText={t('common:actions.cancel')}
        confirmText={t('visitors:confirm.yesUnban')}
        isOpen={!!unbanGuestState}
        cancelCallback={() => setUnbanGuestState(false)}
        confirmCallback={onUnbanConfirm}
        isLoading={isLoading}
      />
      <ConfirmPopup
        title={t('visitors:confirm.areYouSure')}
        className="is-positive"
        body={
          checkInGuestState
            ? t('visitors:confirm.checkInBody', { name: checkInGuestState.name })
            : ''
        }
        cancelText={t('visitors:confirm.no')}
        confirmText={t('visitors:confirm.yes')}
        isOpen={!!checkInGuestState}
        cancelCallback={() => setCheckInGuestState(false)}
        confirmCallback={onCheckInConfirm}
        isLoading={isLoading}
      />
      <ConfirmPopup
        title={t('visitors:confirm.confirmationTitle')}
        className="is-caution"
        body={
          suspendGuestState
            ? t('visitors:confirm.suspendBody', { type: typeLabel, name: suspendGuestState.name })
            : ''
        }
        cancelText={t('common:actions.cancel')}
        confirmText={t('visitors:confirm.yesSuspend')}
        isOpen={!!suspendGuestState}
        cancelCallback={() => setSuspendGuestState(false)}
        confirmCallback={onSuspendConfirm}
        isLoading={isLoading}
      />
      <ConfirmPopup
        title={t('visitors:confirm.confirmationTitle')}
        className="is-caution"
        body={
          unsuspendGuestState
            ? t('visitors:confirm.unsuspendBody', {
                type: typeLabel,
                name: unsuspendGuestState.name,
              })
            : ''
        }
        cancelText={t('common:actions.cancel')}
        confirmText={t('visitors:confirm.yesUnsuspend')}
        isOpen={!!unsuspendGuestState}
        cancelCallback={() => setUnsuspendGuestState(false)}
        confirmCallback={onUnsuspendConfirm}
        isLoading={isLoading}
      />
      <ConfirmPopup
        title={t('visitors:confirm.confirmationTitle')}
        className="is-caution"
        body={
          deleteGuestKeyState
            ? t('visitors:confirm.revokeKeyBody', { name: deleteGuestKeyState.name })
            : ''
        }
        cancelText={t('common:actions.cancel')}
        confirmText={t('visitors:confirm.yesRevokeKey')}
        isOpen={!!deleteGuestKeyState}
        cancelCallback={() => setDeleteGuestKeyState(false)}
        confirmCallback={onDeleteGuestKeyConfirm}
        isLoading={isLoading}
      />
      <GuestDetailsModal guestId={visitorDetailsId} onClose={() => setVisitorDetailsId(null)} />
      <ModalsContainer
        newVisitorModalOpen={newVisitorModalOpen}
        setNewVisitorModalOpen={setNewVisitorModalOpen}
        editingVisitor={editingGuest}
        onCloseModal={() => setEditingGuest(null)}
        onCreateSuccess={() => fetchGuests(search)}
      />
    </div>
  )
}

Guests.propTypes = {
  selectedBuilding: PropTypes.object,
}

export default connect((state) => {
  return {
    selectedBuilding: selectActiveBuilding(state),
  }
})(Guests)
